import React from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useParams, useHistory } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { PlainButton } from 'ui/public/button';
import { LabeledInput, TextInput } from 'ui/public/input';

import { OSInput } from 'ui/standard/input/os_input/os_input';
import { DeviceTypeInput } from 'ui/standard/input/device_type_input/device_type_input';

import { DeviceIcon } from 'ui/devices/device_icon/device_icon';

import { ResultKind } from 'services/base/types';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Host, IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { hostDisplayDeviceType } from 'services/pangolin/hosts_service';

import { Redirect } from 'pages/core/redirect/redirect';

import { EditDeviceStore } from './edit_device_store';

import baseStyle from './edit_device.module.css';
import Messages from './edit_device_messages';

type EditDeviceStyles = {
  headerContainer: string;
  save: string;
  textInput: string;
};

const themedStyles: ThemedStyles<EditDeviceStyles> = {
  [Theme.DAYLIGHT]: {
    headerContainer: baseStyle.headerContainer,
    save: baseStyle.daylightSave,
    textInput: baseStyle.daylightTextInput,
  },
  [Theme.MIDNIGHT]: {
    headerContainer: baseStyle.headerContainer,
    save: baseStyle.midnightSave,
    textInput: baseStyle.midnightTextInput,
  },
};

type BaseEditDeviceProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  host: Host;
};

export const BaseEditDevice = observer(({ pangolin, domain, host }: BaseEditDeviceProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new EditDeviceStore(host));
  React.useEffect(() => { setStore(new EditDeviceStore(host)); }, [host]);

  const handleSave = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(domain));

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      history.goBack();
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, domain, history]);

  return (
    <StandardPage>
      <form onSubmit={handleSave}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <PlainButton type='submit'
            busy={store.busy} reserveBusyIconArea={true}
            disabled={!pangolin.onlineStatus}
            className={styles.save}>{Messages.save()}</PlainButton>
        </div>
        <DeviceIcon type={hostDisplayDeviceType(host, {
          overrideCustomDeviceType: true,
          customDeviceType: store.deviceType.deviceType,
        })}/>
        <div>
          <LabeledInput label={Messages.deviceNameLabel()}>
            <TextInput store={store.deviceName} placeholder={host.name}/>
          </LabeledInput>
          <LabeledInput label={Messages.deviceTypeLabel()}>
            <DeviceTypeInput store={store.deviceType}/>
          </LabeledInput>
          <LabeledInput label={Messages.operatingSystemLabel()}>
            <OSInput store={store.deviceOS}/>
          </LabeledInput>
        </div>
      </form>
    </StandardPage>
  );
});

export const EditDevice = observer(() => {
  const { pangolinID, deviceID } = useParams<{ pangolinID: string, deviceID: string }>();
  if (!pangolinID) return <Redirect />;
  if (!deviceID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  const host = domain.hosts.get(deviceID);
  if (!host) return <Redirect />;

  return (
    <BaseEditDevice
      pangolin={pangolin}
      domain={domain}
      host={host}
    />
  );
});
